import React, { useState, useEffect } from 'react';
import DefaultLayout from '../../layouts/DefaultLayout';
import TemplateColumn from '../../layouts/TemplateColumn';
import { PageHeader, RelatedContent, PrimaryCallOut, ReferencesBlock, TextBlock, ResourceLibrary, AnchorLinks, DefinitionList } from '../../components';
import { StaticImage } from 'gatsby-plugin-image';
import { jobCodes } from '../../util/data/jobCodes';
import { Heading } from '../../util/typography';
import { createMarkup, stringToSlug } from '../../util/functions';
import styled from 'styled-components';
import PageSection from '../../components/layout/PageSection';
import "./hcp-optimize.css";
import ResourceCustomSlider from '../../components/ResourceCustomSlider';
import { ANCHOR_LINK_SCROLL_OFFSET } from '../../constants/layout';

// SEO INFORMATION
const pageMeta = {
    title: `Resource Center for KEYTRUDA® (pembrolizumab) | HCP`,
    keywords: `resources, surgeons, patients, health care professionals`,
    description: `Health care professionals and surgeons can access downloadable resources, including case studies, to use and share with their patients during treatment with KEYTRUDA® (pembrolizumab).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Biomarker Eligibility Brochure","description":"A guide to biomarker testing and cut points for relevant tumor types and indications."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Patient Monitoring Checklist","description":"Identify and monitor some of the signs and symptoms associated with adverse reactions related to treatment with KEYTRUDA."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"A Guide to Your Cancer Care Team","description":"This guide can help patients understand the role of each specialist on their cancer support team and the importance of taking an active part in their own care."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Hypothetical Patient Profile Management of Immune-Mediated Nephritis","description":"Review a hypothetical patient profile that informs the monitoring and management of immune-mediated nephritis."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Hypothetical Patient Profile Management of Immune-Mediated Pneumonitis","description":"Review a hypothetical patient profile that informs the monitoring and management of immune-mediated pneumonitis."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Hypothetical Patient Profile Management of Immune-Mediated Hypothyroidism","description":"Review a hypothetical patient profile that informs the monitoring and management of immune-mediated hypothyroidism."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Signs and Symptoms of Select Immune-Mediated Adverse Reactions (IMARs) and Other Adverse Reactions","description":"Signs and symptoms of select immune-mediated adverse reactions (IMARs) and other adverse reactions for KEYTRUDA."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"KEYTRUDA: A Treatment Guide for Urologists","description":"A guide to select indications, mechanism of action, dosing options and modifications, preparation/administration, and monitoring/management of adverse reactions."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Surgeon Brochure for KEYTRUDA","description":"A resource to help you identify whether certain patients with NSCLC, TNBC, RCC, or melanoma are eligible for KEYTRUDA, learn about the mechanism of action for KEYTRUDA, and get tips on collaborating with oncologists."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"KEYTRUDA: End Points Brochure","description":"Learn more about the end points and study designs used in the clinical trials evaluating KEYTRUDA for the treatment of certain types of NSCLC, TNBC, RCC, and melanoma."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Signs and Symptoms of Select IMARs and Other Adverse Reactions Brochure","description":"In this resource, you can review certain signs and symptoms of select IMARs and other adverse reactions that patients should report. The list presented in the brochure is not exhaustive."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Understanding the Mechanism of Action for KEYTRUDA® (pembrolizumab)","description":"A guide to help understand the mechanism of action for KEYTRUDA."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Build Your Support Community Guide","description":"This guide can help patients build a strong support community, and includes lists for them to fill out on who can help them with their needs."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Doctor Conversation Guide: Questions to Ask Before Starting Treatment","description":"A guide for patients and caregivers with information and discussion points about starting treatment with KEYTRUDA."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Doctor Conversation Guide: Questions to Ask While on Treatment","description":"This guide for patients includes information and important questions to ask during the entire treatment journey, from just starting out to when treatment is finished."}`,
        `{"@context":"https://schema.org/","url":"https://www.keytrudahcp.com/resources/hcp-and-patient-resources/","@type":"MedicalWebPage","name":"Food, Communication, and Confidence Guide","description":"Information for patients and caregivers about managing meals during treatment, and how to deal with feelings and social interactions that revolve around food."}`
    ]
}

const jobCode = jobCodes[31].jobCode;


const definitionsForSurgeons = `
    NSCLC = non&ndash;small cell lung cancer; TNBC = triple-negative breast cancer; RCC = renal cell carcinoma.
`;

const earlierStageIndicationsForSurgeons = [{
    items: [
        {
            title: 'NSCLC',
            content: '<ul><li>KEYTRUDA is indicated for the treatment of patients with resectable (tumors ≥4 cm or node positive) non&ndash;small cell lung cancer (NSCLC) in combination with platinum-containing chemotherapy as neoadjuvant treatment, and then continued as a single agent as adjuvant treatment after surgery.</li><li>KEYTRUDA, as a single agent, is indicated as adjuvant treatment following resection and platinum-based chemotherapy for adult patients with stage IB (T2a ≥4 cm), II, or IIIA non&ndash;small cell lung cancer (NSCLC).</li>'
        },
        {
            title: 'TNBC',
            content: '<ul><li>KEYTRUDA is indicated for the treatment of patients with high-risk early-stage triple-negative breast cancer (TNBC) in combination with chemotherapy as neoadjuvant treatment, and then continued as a single agent as adjuvant treatment after surgery.</li>'
        },
        {
            title: 'RCC',
            content: '<ul><li>KEYTRUDA is indicated for the adjuvant treatment of patients with renal cell carcinoma (RCC) at intermediate-high or high risk of recurrence following nephrectomy, or following nephrectomy and resection of metastatic lesions.</li>'
        },
        {
            title: 'Melanoma',
            content: '<ul><li>KEYTRUDA is indicated for the adjuvant treatment of adult and pediatric (12 years and older) patients with stage IIB, IIC, or Ill melanoma following complete resection.</li>'
        }
    ]
}];

const anchorLinkModelData = [
    { label: 'Featured Resources' },
    { label: 'For Health Care Professionals (HCPs)', labelTextOverride: 'For Health Care <span class="line-break"></span> Professionals (HCPs)'},
    { label: 'For Surgeons' },
    { label: 'For Your Patients' }
]

const relatedContent = [
    {
        section: 'RESOURCES',
        link: {
            url: '/efficacy/',
            label: 'Efficacy'
        }
    },
    {
        section: 'RESOURCES',
        link: {
            url: '/resources/mechanism-of-action/',
            label: 'Mechanism of Action'
        }
    }, {
        section: 'RESOURCES',
        link: {
            url: '/resources/key-you-patient-support-program/',
            label: 'Patient Support Program'
        }
    },
]


const resourceCard = [
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/biomaker.png" alt="Download a Biomarker Eligibility Brochure for Treatment With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        ),
        title: 'Biomarker Eligibility Brochure',
        body: "A guide to biomarker testing and cut points for relevant tumor types and indications.",
        href: '/static/pdf/Biomarker_Eligibility_Brochure_Digital.pdf',
        buttonLabel: 'View Brochure',
        target: "_blank",
        isPDFOpen: true
    },
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/patient-checklist.png" alt="Download Patient Monitoring Checklist for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        ),
        title: 'Patient Monitoring Checklist',
        body: "Identify and monitor some of the signs and symptoms associated with adverse reactions related to treatment with KEYTRUDA.",
        href: '/static/pdf/Patient_Monitoring_Checklist.pdf',
        buttonLabel: 'View Checklist',
        target: "_blank",
        isPDFOpen: true,
        disabled:true
    },
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/guide-care-team.png" alt="Download a Guide to Help Patients Understand the Role of Each Specialist on a Cancer Care Team" width={700} height={400} />
        ),
        title: 'A Guide to Your Cancer Care Team',
        body: "This guide can help patients understand the role of each specialist on their cancer support team and the importance of taking an active part in their own care.",
        href: '/static/pdf/A_Guide_to_Your_Cancer_Care_Team.pdf',
        buttonLabel: 'View Guide',
        target: "_blank",
        isPDFOpen: true,
    },
];

const HCPitem =
    [
        // {

        //     image: () => (
        //         <img src="/static/images/hcp-patient-resources/Martin-Patient-Profile.png" alt="Download a Case Study of a Hypothetical Patient Diagnosed With Pathological Prognostic Stage IVA (T3N2M0) p16-negative Oropharyngeal Squamous Cell Carcinoma Treated With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        //     ),
        //     title: 'Clinical Case Study: Hypothetical Patient with Recurrent, Metastatic Head and Neck Squamous Cell Carcinoma',
        //     body: "Review details of this hypothetical patient's clinical case.",
        //     url: '/static/pdf/HCP_Patient_Profile_with_Recurrent_HNSCC.pdf',
        //     buttonLabel: 'Download Case Study',
        // },
        // {

        //     image: () => (
        //         <img src="/static/images/hcp-patient-resources/Diane-Patient-Profile.png" alt="Download a Case Study of a Hypothetical Patient Diagnosed With Stage IVA (T1N2M0) Laryngeal Squamous Cell Carcinoma Treated With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        //     ),
        //     title: 'Clinical Case Study: Hypothetical Patient with Unresectable, Recurrent Head and Neck Squamous Cell Carcinoma',
        //     body: "Read this clinical case to learn more about this hypothetical patient's history, prior oncology treatment, and adverse event management plan.",
        //     url: '/static/pdf/HCP_Patient_Profile_with_Unresectable_HNSCC.pdf',
        //     buttonLabel: 'Download Case Study',
        // },
        // {

        //     image: () => (
        //         <img src="/static/images/hcp-patient-resources/Diane-Patient-Profile2.png" alt="Download Select NCCN Clinical Practice Guidelines for Head and Neck Cancers" width={700} height={400} />
        //     ),
        //     title: 'Select Guidelines for Patients with Recurrent, Unresectable, or Metastatic Head and Neck Cancers',
        //     body: "Review what these guidelines recommend for certain patients with head and neck cancers.",
        //     url: '/static/pdf/HCP_NCCN_Resource_HNSCC.pdf',
        //     buttonLabel: 'Download Guidelines',
        // },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/Nephritis.png" alt="Download a Guide on How to Manage Immune-Mediated Nephritis During Treatment With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Hypothetical Patient Profile Management of Immune-Mediated Nephritis',
            body: "Review a hypothetical patient profile that informs the monitoring and management of immune-mediated nephritis.",
            href: '/static/pdf/Hypothetical_Patient_Profile_Management_of_Immune_Mediated_Nephritis.pdf',
            buttonLabel: 'View Profile',
            target: "_blank",
            isPDFOpen: true,
            disabled: true
        },
        {
            image: () => (
                <img src="/static/images/hcp-patient-resources/Pneumonitis.png" alt="Download a Guide on How to Manage Immune-Mediated Pneumonitis During Treatment With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Hypothetical Patient Profile Management of Immune-Mediated Pneumonitis',
            body: "Review a hypothetical patient profile that informs the monitoring and management of immune-mediated pneumonitis.",
            href: '/static/pdf/Hypothetical_Patient_Profile_Management_of_Immune_Mediated_Pneumonitis.pdf',
            buttonLabel: 'View Profile',
            target: "_blank",
            isPDFOpen: true,
        },
        {
            image: () => (
                <img src="/static/images/hcp-patient-resources/Hypothyroidism.png" alt="Download a Guide on How to Manage Immune-Mediated Hypothyroidism During Treatment With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Hypothetical Patient Profile Management of Immune-Mediated Hypothyroidism',
            body: "Review a hypothetical patient profile that informs the monitoring and management of immune-mediated hypothyroidism.",
            href: '/static/pdf/Hypothetical_Patient_Profile_Management_of_Immune_Mediated_Hypothyroidism.pdf',
            buttonLabel: 'View Profile',
            target: "_blank",
            isPDFOpen: true,
            disabled: true
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/biomaker.png" alt="Download a Biomarker Eligibility Brochure for Treatment With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Biomarker Eligibility Brochure',
            body: "A guide to biomarker testing and cut points for relevant tumor types and indications.",
            href: '/static/pdf/Biomarker_Eligibility_Brochure_Digital.pdf',
            buttonLabel: 'View Brochure',
            target: "_blank",
            isPDFOpen: true
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/Biomarker-Eligibility-Brochure1.png" alt="Download Information on Adverse Reactions With KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Signs and Symptoms of Select Immune-Mediated Adverse Reactions (IMARs) and Other Adverse Reactions',
            body: "Signs and symptoms of select immune-mediated adverse reactions (IMARs) and other adverse reactions for KEYTRUDA.",
            href: '/static/pdf/Signs_and_Symptoms_of_Select_IMARs_and_Other_ARs_Brochure.pdf',
            buttonLabel: 'View Brochure',
            target: "_blank",
            isPDFOpen: true,
            disabled:true
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/patient-checklist.png" alt="Download Patient Monitoring Checklist for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Patient Monitoring Checklist',
            body: "Identify and monitor some of the signs and symptoms associated with adverse reactions related to treatment with KEYTRUDA.",
            href: '/static/pdf/Patient_Monitoring_Checklist.pdf',
            buttonLabel: 'View Checklist',
            target: "_blank",
            isPDFOpen: true,
            disabled:true
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/guide-urologists.png" alt="Download an Urologist Guide to Select Indications, MOA, and Dosing Information for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'KEYTRUDA: A Treatment Guide for Urologists',
            body: "A guide to select indications, mechanism of action, dosing options and modifications, preparation/administration, and monitoring/management of adverse reactions.",
            href: '/static/pdf/US-KEY-08326.pdf',
            buttonLabel: 'View Brochure',
            target: "_blank",
            isPDFOpen: true,
            isButtonDisable: 'true',
            disabled:true
        },
        {
            image: () => (
                <img src="/static/images/hcp-patient-resources/hypothetical-patient-profile-management-of-rash-maculo-papular.png" alt="Download a Hypothetical Patient Profile on the Management of Rash Maculo-Papular" width={700} height={400} />
            ),
            title: 'Hypothetical Patient Profile Management of Rash Maculo-Papular',
            body: "Review a hypothetical patient profile that informs the monitoring and management of rash maculo-papular.",
            href: '/static/pdf/Hypothetical-Patient-Profile_Management-of-Rash-Maculo-Papular.pdf',
            buttonLabel: 'View Profile',
            target: "_blank",
            isPDFOpen: true,
            disabled:true
        },
        {
            image: () => (
                <img src="/static/images/hcp-patient-resources/guide-for-keytruda.png" alt="Download a Comprehensive Guide to Information About KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'Guide for KEYTRUDA',
            body: "A comprehensive guide to information about KEYTRUDA dosing, administration, ordering, and support.",
            href: '/static/pdf/guide-for-keytruda.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
            disabled:true
        },
        {
            image: () => (
                <img src="/static/images/hcp-patient-resources/keytruda-dosing-and-testing-requirements.png" alt="Download a Guide to Dosing and Testing Requirements for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
            ),
            title: 'KEYTRUDA: Dosing and Testing Requirements',
            body: "A guide covering select indications, testing requirements, dosing, and duration of treatment for KEYTRUDA.",
            href: '/static/pdf/Dosing_and_Testing_Requirements.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
        },
    ]

const SurgeonItem = [
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/Biomarker_Eligibility_Brochure.png" alt="Download a Surgeon Brochure to Identify Whether Certain Patients With NSCLC, TNBC, RCC, and Melanoma are Eligible for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        ),
        title: 'Surgeon Brochure for KEYTRUDA',
        body: "A resource to help you identify whether certain patients with NSCLC, TNBC, RCC, or melanoma are eligible for KEYTRUDA, learn about the mechanism of action for KEYTRUDA, and get tips on collaborating with oncologists.",
        href: '/static/pdf/Surgeon_Brochure_For_Keytruda.pdf',
        buttonLabel: 'View Brochure',
        target: "_blank",
        isPDFOpen: true,
        disabled:true
    },
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/Endpoint_Brochure.png" alt="Download a Surgeon End Points Brochure for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        ),
        title: 'KEYTRUDA: End Points Brochure',
        body: "Learn more about the end points and study designs used in the clinical trials evaluating KEYTRUDA for the treatment of certain types of NSCLC, TNBC, RCC, and melanoma.",
        href: '/static/pdf/End_Points_Brochure.pdf',
        buttonLabel: 'View Brochure',
        target: "_blank",
        isPDFOpen: true,
        disabled:true
    },
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/Signs_and_Symptoms_Brochure.png" alt="Signs and Symptoms of Select IMARs and Other Adverse Reactions Brochure" width={700} height={400} />
        ),
        title: 'Signs and Symptoms of Select IMARs and Other Adverse Reactions Brochure',
        body: "In this resource, you can review certain signs and symptoms of select IMARs and other adverse reactions that patients should report. The list presented in the brochure is not exhaustive.",
        href: '/static/pdf/Signs_and_Symptoms_of_Select_IMARs_and_Other_ARs_Brochure.pdf',
        buttonLabel: 'View Brochure',
        target: "_blank",
        isPDFOpen: true,
        disabled:true
    },
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/Understanding_the_Mechanism_of_Action_for_KEYTRUDA.png" alt="Download a Guide to Mechanism Of Action for KEYTRUDA® (pembrolizumab)" width={700} height={400} />
        ),
        title: <>Understanding the Mechanism of Action for KEYTRUDA®<br />(pembrolizumab)</>,
        body: "A guide to help understand the mechanism of action for KEYTRUDA.",
        href: '/static/pdf/Understanding_the_Mechanism_of_Action_for_KEYTRUDA.pdf',
        buttonLabel: 'View Brochure',
        target: "_blank",
        isPDFOpen: true,
        disabled:true
    },
    /*
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/Mechanism.png" alt="ADT HCP Martin Patient Profile" width={700} height={400} />
        ),
        title: 'ADT HCP Martin Patient Profile',
        body:"Review Martin's clinical case—a hypothetical patient with recurrent, metastatic head and neck squamous cell carcinoma.",
        url: '/static/pdf/US-OHN-01974_ADT_HCP_Martin_Patient_Profile_PDF_HNSCC_L07_Functional_Secure.pdf',
        buttonLabel: 'Download Profile',
    },
    {
        image: () => (
            <img src="/static/images/hcp-patient-resources/Mechanism.png" alt="ADT HCP Diane Patient Profile" width={700} height={400} />
        ),
        title: 'ADT HCP Diane Patient Profile',
        body:"Review Diane's clinical case—a hypothetical patient with recurrent, metastatic head and neck squamous cell carcinoma.",
        url: '/static/pdf/US-OHN-01975_ADT_HCP_Diane_Patient_Profile_PDF_HNSCC_L11_Functional_Secure.pdf',
        buttonLabel: 'Download Profile',
    },
    */
]
const NursesItem =
    [
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/pdf-image.png" alt="pdf-image" width={700} height={400} />
            ),
            title: 'Borem ipsum dolor sit amet consectetur',
            body: "Norem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.",
            url: '#',
            buttonLabel: 'Download Resource',
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/pdf-image.png" alt="pdf-image" width={700} height={400} />
            ),
            title: 'Yorem ipsum dolor sit amet consectetur',
            body: "Torem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.",
            url: '#',
            buttonLabel: 'Download Resource',
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/pdf-image.png" alt="pdf-image" width={700} height={400} />
            ),
            title: 'Porem ipsum dolor sit amet consectetur',
            body: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc vulputate libero et velit interdum, ac aliquet odio mattis. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos.",
            url: '#',
            buttonLabel: 'Download Resource',
        },

    ]
const PatientItem =
    [
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/patient-guide-care-team.png" alt="Download a Guide to Help Patients Understand the Role of Each Specialist on a Cancer Care Team" width={700} height={400} />
            ),
            title: 'A Guide to Your Cancer Care Team',
            body: "This guide can help patients understand the role of each specialist on their cancer support team and the importance of taking an active part in their own care.",
            href: '/static/pdf/A_Guide_to_Your_Cancer_Care_Team.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/patient-support-community.png" alt="Download a Guide on How to Build Your Support Community During Cancer Treatment" width={700} height={400} />
            ),
            title: 'Build Your Support Community Guide',
            body: "This guide can help patients build a strong support community, and includes lists for them to fill out on who can help them with their needs.",
            href: '/static/pdf/Build_Your_Support%20_Community_Guide.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/patient-doctor-conversation-guide-start.png" alt="Download a Guide on How to Have a Better Conversation With Your Doctor Before, During and After the Appointment" width={700} height={400} />
            ),
            title: 'Doctor Conversation Guide: Questions to Ask Before Starting Treatment',
            body: "A guide for patients and caregivers with information and discussion points about starting treatment with KEYTRUDA.",
            href: '/static/pdf/Doctor_Conversation_Tool_Guide_Questions_to_Ask_Before_Starting%20_Treatment.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/patient-doctor-conversation-guide-while.png" alt="Download a Guide on What to Discuss With Your Doctor Throughout Cancer Treatment" width={700} height={400} />
            ),
            title: 'Doctor Conversation Guide: Questions to Ask While on Treatment',
            body: "This guide for patients includes information and important questions to ask during the entire treatment journey, from just starting out to when treatment is finished.",
            href: '/static/pdf/Doctor_Conversation_Guide_Questions_to_Ask_While_on_Treatment.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
        },
        {

            image: () => (
                <img src="/static/images/hcp-patient-resources/patient-food.png" alt="Download a Guide on Managing Meals, Communication, and Confidence During Cancer Treatment" width={700} height={400} />
            ),
            title: 'Food, Communication, and Confidence Guide',
            body: "Information for patients and caregivers about managing meals during treatment, and how to deal with feelings and social interactions that revolve around food.",
            href: '/static/pdf/Food_Communication_and_Confidence_Guide.pdf',
            buttonLabel: 'View Guide',
            target: "_blank",
            isPDFOpen: true,
        },

    ]

const pageReferences = [
    {
        label: "1.",
        text: "Berardi R, Morgese F, Rinaldi S, et al. Benefits and limitations of a multidisciplinary approach in cancer patient management. <em>Cancer Manag Res.</em> 2020;12:9363-9374. doi:10.2147/CMAR.S220976"
    },
    {
        label: "2.",
        text: "Surgical oncologist. City of Hope. Updated April 7, 2023. Accessed July 13, 2023. https://www.cancercenter.com/physician-directory/types-oncologists/surgical-oncologists"
    },


]
const resourceLibraryItemsForPatients = [
    {
        label: 'Surgeon Brochure for KEYTRUDA',
        url: '/static/pdf/US-KEY-08326.pdf',
        resourceBodyText: 'A resource to help you identify patients eligible for KEYTRUDA in NSCLC, TNBC, RCC, or melanoma, learn about the mechanism of action for KEYTRUDA, and get tips on collaborating with oncologists.',
        image: () => (
            <StaticImage src="../../assets/keytruda-surgeon-brochure.png" placeholder="TRACED_SVG" alt="Download a Surgeon Brochure to Identify Eligible Patients for KEYTRUDA® (pembrolizumab) in NSCLC, TNBC, RCC, and Melanoma" />
        ),
    },
    {
        label: 'KEYTRUDA: End Points Brochure',
        url: '/static/pdf/End_Points_Brochure.pdf',
        resourceBodyText: 'Learn more about the end points and study designs used in the clinical trials for KEYTRUDA in NSCLC, TNBC, RCC, and melanoma.',
        image: () => (
            <StaticImage src="../../assets/key-endpoints-brochure.png" placeholder="TRACED_SVG" alt="Download a Surgeon End Points Brochure for KEYTRUDA® (pembrolizumab) in NSCLC, TNBC, RCC, and Melanoma" />
        ),
    },
    {
        label: 'Signs and Symptoms of Select Immune-Mediated Adverse Reactions (IMARs) and Other Adverse Reactions Brochure',
        disabled:true,
        url: '/static/pdf/Signs_and_Symptoms_of_Select_IMARs_and_Other_ARs_Brochure.pdf',
        resourceBodyText: 'In this resource, you can review some select signs and symptoms of select IMARs and other adverse reactions that patients should report. The list presented in the brochure is not exhaustive.',
        image: () => (
            <StaticImage src="../../assets/multidisciplinary-care-coordination-resource.png" placeholder="TRACED_SVG" alt="Download a Resource to Review Signs and Symptoms of Select Immune-Mediated Adverse Reactions (IMARs) and Other Adverse Reactions Brochure for KEYTRUDA® (pembrolizumab)" />
        ),
    },
]

const PageSectionTitle = styled(Heading.H4)`
text-align: center;

@media screen and (max-width: 665px) {
        font-size: 20px;
        line-height: 26px;
}

`;

const AnchorTarget = styled.span`
    display: block;
    height: 0;
    overflow: hidden;
    line-height:none;
    transform: translateY(-${ANCHOR_LINK_SCROLL_OFFSET}); //manage offset for fully-expanded sticky nav
`;


const SliderSubSectionWrapper = styled.div`
      @media (max-width:1024px) {
            padding-left: 20px;
            padding-right: 20px;
    }
`;
/* Break to a new line at screen widths below 768px */


const Page = ({ location }) => {

    // AT/screenshots hook to disable all Tertiary Buttons and resourceBodyText links
    const [allResourcesDisabled, setAllResourcesDisabled] = useState(false);
    useEffect(() => {
        const disableHandler = (event) => {
            if (event.data.action === 'disableAllResources') {
                setAllResourcesDisabled(true);
            }
        };

        window.addEventListener("message", disableHandler);
        return () => {
            window.removeEventListener("message", disableHandler);
        };
    }, []);

    return (
        
        <DefaultLayout pageMeta={pageMeta} location={location} jobCode={jobCode}>
                <div className='hcp-page-layout'>
                <div data-template-name="template-c">
                    {/* Begin Intro Column */}
                    <TemplateColumn id="template-c-intro">
                        <PageHeader title="Resource Center" />

                        <PageSection
                            bgColor="cloud"
                            className= "hcp-anchor-links"
                        >
                            <AnchorLinks items={anchorLinkModelData} />
                        </PageSection>

                    </TemplateColumn>
                    {/* End Intro Column */}
                    {/* Begin Main Column */}
                    <TemplateColumn id="template-c-main" >
                        {/* <SliderSectionsWrapper> */}
                        <PageSection customClasses="mobSpaceRemove" centerTitle >
                        <AnchorTarget id={stringToSlug(anchorLinkModelData[0].label)}/>
                            <PageSectionTitle >{anchorLinkModelData[0].label}</PageSectionTitle>
                            <ResourceCustomSlider className="removeSpacer" items={resourceCard} sliderId={"featured"} dataDesignModule={"featured_content"} overlayShadowType="white" />
                        </PageSection>

                        <PageSection customClasses="mobSpaceRemove" bgColor="cloud" className="professionals" >
                        <AnchorTarget id={stringToSlug(anchorLinkModelData[1].label)}/>
                            <PageSectionTitle >For HCPs</PageSectionTitle>
                            <ResourceCustomSlider items={HCPitem} sliderId={"professionals"} dataDesignModule={"hcp_resources"}
                            overlayShadowType="cloud"
                            />
                        </PageSection>
                   
                        {/* <PageSection title="Resources for Surgeons" bgColor="cloud">
                    <TextBlock>
                            <p>As a surgeon, you play a critical role in developing comprehensive treatment plans for patients. The resources below can help you collaborate with your patient’s medical oncologist and multidisciplinary team (MDT) to determine if your patient is eligible for neoadjuvant and adjuvant therapy or adjuvant therapy only, together with surgery.<sup>1,2</sup></p>
                
                        </TextBlock>
                        <ResourceLibrary items={resourceLibraryItemsForPatients} allResourcesDisabled={allResourcesDisabled} />
                    </PageSection> */}

                        <PageSection customClasses="mobSpaceRemove" list='surgeon'>
                          <AnchorTarget id={stringToSlug(anchorLinkModelData[2].label)}/>
                            <PageSectionTitle >{anchorLinkModelData[2].label}</PageSectionTitle>
                            <SliderSubSectionWrapper>
                            <DefinitionList sections={earlierStageIndicationsForSurgeons} subhead='Earlier-Stage Indications' maxWidth='860px' list='surgeon' />
                            </SliderSubSectionWrapper>
                             <ResourceCustomSlider items={SurgeonItem} sliderId={"surgeon"} dataDesignModule={"surgeon_resources"}
                            overlayShadowType="white"
                            />
                        </PageSection>
                        {/* <PageSection bgColor="cloud">
                    <HeadingH3
                        as='H3'
                        dangerouslySetInnerHTML={createMarkup("For Nurses")}
                    />
                        <ResourceOwlSilder items={NursesItem} sliderId={"nurses"} />
                    </PageSection> */}
                        <PageSection customClasses="mobSpaceRemove" bgColor="cloud">
                        <AnchorTarget id={stringToSlug(anchorLinkModelData[3].label)}/>
                            <PageSectionTitle >{anchorLinkModelData[3].label}</PageSectionTitle>
                             <ResourceCustomSlider items={PatientItem} sliderId={"patient"} dataDesignModule={"hcp_resources"}
                            overlayShadowType="patient_resources"
                            />
                        </PageSection>
                {/* </SliderSectionsWrapper> */}

                        <PageSection bgColor='keytrudaGreen'>
                            <RelatedContent items={relatedContent} />
                        </PageSection>
                    </TemplateColumn>
                    {/* End Main Column */}
                </div>
        </div>
            </DefaultLayout>
    );
};

export default Page;
